import { getVariableColor } from '@/composables/theme'
interface Tag {
  raw: string
  tag: string
  index: number
  color: [string, string]
}

type TagMap = Record<string, Tag>
export type TagsReplaceData = Record<string, string>
export type TagParser = (tag: Tag) => (data: string) => string

function clearTag(tag: string) {
  return tag.trim().replaceAll(/[\s\n]/gi, '_').toLowerCase()
}

interface GenetrateTemplateOptions {
  tagMarker: '#' | '@' | '$' | '!' | '%'
  displayParser?: TagParser
}

const DEFAULT_OPTIONS: GenetrateTemplateOptions = {
  tagMarker: '#',
}

export function generateTagsHtml(tag: Tag) {
  const color = tag.color

  const styles = `
    color: ${color[0]};
    background-color: ${color[1]};
    border-radius: 4px;
  `.replace(/\s/g, '')

  return (data: string) =>
    `&nbsp;<span style="${styles}" data-tag="${tag}">#${data}</span>`
}

function getTags(data: RegExpStringIterator<RegExpExecArray>) {
  const keys: TagMap = {}
  let currentMatchIndex = 0
  for (const match of data) {
    const [raw, tag] = match
    const parsedTag = clearTag(tag)
    if (keys[parsedTag])
      continue
    keys[parsedTag] = {
      raw: raw.trim().replaceAll(/[\[\]\#]/gi, '').toLowerCase(),
      tag: parsedTag,
      index: currentMatchIndex,
      color: getVariableColor(currentMatchIndex),
    }
    currentMatchIndex++
  }

  return keys
}

export function generateTemplate(raw: string | null, options = DEFAULT_OPTIONS) {
  if (!raw)
    return null
  if (typeof raw !== 'string')
    throw new Error('Template must be as string')
  const templatePattern = new RegExp(`${options.tagMarker}\\[(.*?)\\]`, 'gi')
  const tagData = getTags(raw.matchAll(templatePattern))
  const keys = Object.keys(tagData)
  const templateMask = raw.replaceAll(templatePattern, (_, tag) => {
    const tagClear = clearTag(tag)
    if (tagData[tagClear])
      return options.displayParser ? options.displayParser(tagData[tagClear])(tagData[tagClear].tag) : `#${tagData[tagClear].raw}`
    return tagClear
  })

  const transformer = (keysAndData: TagsReplaceData, parser?: TagParser) => {
    return raw.replaceAll(templatePattern, (_, tag) => {
      const tagClear = clearTag(tag)
      const currentTagData = tagData[tagClear]
      if (!currentTagData)
        return tag

      const content = (keysAndData[tagClear]?.trim() !== '') ? keysAndData[tagClear] : currentTagData.raw

      return parser ? parser(currentTagData)(content) : content
    }).replace(/[\n\r]+/g, '<br>')
  }

  return { transformer, tagData, display: templateMask, raw, variables: keys }
}
